import React, { useState, useEffect, useCallback } from 'react';
import VerificationInput from 'react-verification-input';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, Box, Typography, CircularProgress } from '@mui/material';
import OlcButton from '~/components/General/OlcButton';
import { verify2FACode, resend2FACode } from '~/redux/actions/two-factor-actions';
import { failure } from '~/redux/actions/snackbar-actions';
import { CLEAR_2FA_STATE } from '~/redux/actions/action-types';
import { consumePostLoginPath } from '~/utils/helperFunctions';
import './styles.scss';

const RESEND_COOLDOWN = 60;

const TwoFactorVerificationModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { requires2FA, tempToken, maskedEmail } = useSelector((state) => state.twoFactorReducer);

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!requires2FA) return;
    setCooldown(RESEND_COOLDOWN);
    setCode('');
    setError('');
  }, [requires2FA]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleVerify = useCallback(
    async (verifyCode = code) => {
      if (verifyCode.length !== 6 || loading) return;
      setError('');
      setLoading(true);
      const result = await dispatch(verify2FACode(verifyCode, tempToken));
      setLoading(false);
      if (result?.message === 'success') {
        setCode('');
        navigate(consumePostLoginPath());
      } else {
        const msg = result?.response?.data?.message || 'Verification failed';
        setError(msg);
        setCode('');
      }
    },
    [code, tempToken, loading, dispatch, navigate]
  );

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setError('');
    const result = await dispatch(resend2FACode(tempToken));
    setResending(false);
    if (result?.message === 'success') {
      setCooldown(RESEND_COOLDOWN);
    } else {
      const msg = result?.response?.data?.message || 'Failed to resend code';
      dispatch(failure(msg));
    }
  };

  const handleCancel = () => {
    dispatch({ type: CLEAR_2FA_STATE });
    setCode('');
    setError('');
    consumePostLoginPath();
    navigate('/login');
  };

  if (!requires2FA) return null;

  return (
    <Dialog
      open={requires2FA}
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      className="twoFactorModal twoFactorVerificationModal"
    >
      <DialogContent className="twoFactorContent">
        <Box className="twoFactorHeader">
          <Typography variant="h5">Verify Your Identity</Typography>
          <Typography variant="body2" className="twoFactorSubtext">
            We sent a 6-digit code to <strong>{maskedEmail}</strong>
          </Typography>
        </Box>

        <Box className="twoFactorInputWrapper">
          <VerificationInput
            classNames={{
              container: 'twoFactorInputContainer',
              character: 'twoFactorCharacter',
              characterInactive: 'twoFactorCharacter--inactive',
              characterSelected: 'twoFactorCharacter--selected',
            }}
            placeholder=""
            length={6}
            value={code}
            autoFocus
            onChange={(val) => {
              setCode(val);
              setError('');
            }}
            onComplete={(val) => handleVerify(val)}
          />
        </Box>

        {error && <Typography className="twoFactorError">{error}</Typography>}

        <Box className="twoFactorActions">
          <OlcButton
            variant="secondary"
            className="twoFactorResendBtn"
            disabled={cooldown > 0 || resending}
            onClick={handleResend}
          >
            {resending ? 'Sending...' : cooldown > 0 ? `Resend Code (${cooldown}s)` : 'Resend Code'}
          </OlcButton>
          <OlcButton
            variant="primary"
            className="twoFactorSubmitBtn"
            disabled={code.length !== 6 || loading}
            onClick={() => handleVerify()}
          >
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '22px !important',
                  height: '22px !important',
                }}
              />
            ) : (
              'Verify'
            )}
          </OlcButton>
        </Box>
        <button type="button" className="twoFactorCancelLink" onClick={handleCancel}>
          Back to Login
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorVerificationModal;
